import type { LoyaltyReward } from "./loyalty-rewards.types";

const DISCOUNT_LINE_TYPE = 1;

/**
 * CTA state for a single loyalty reward.
 *
 * - "applied":  the reward is discounting the cart (only then is there a line to remove)
 * - "pending":  EVA holds the coupon on the order but it is not discounting yet
 * - "noCoupon": catalog promotion the user owns no coupon for, nothing to apply
 * - "empty":    no products in the cart yet - nothing can be applied
 * - "blocked":  another coupon is already applied (one coupon at a time)
 * - "apply":    applicable to the current cart
 * - "disabled": not applicable to the current cart (requirements not met / coupon not valid yet)
 */
export type RewardCta = "applied" | "pending" | "noCoupon" | "empty" | "blocked" | "apply" | "disabled";

export function getRewardLabel(reward: LoyaltyReward): string {
  return reward.PromotionLabel || reward.MarketingDescription || reward.Description || "";
}

export function getRewardCouponCode(reward: LoyaltyReward): string | undefined {
  return reward.coupons.find((coupon) => coupon.available)?.code ?? reward.coupons[0]?.code;
}

export function getApplicableCouponCode(reward: LoyaltyReward): string | undefined {
  return reward.coupons.find((coupon) => coupon.available)?.code;
}

type CartLineLike = { Type?: number; ID?: string | number; DiscountBackendID?: string; DiscountCouponCode?: string };

export function findRewardDiscountLineId(lines: CartLineLike[], reward: LoyaltyReward): string | undefined {
  const line = lines.find(
    (candidate) =>
      candidate.Type === DISCOUNT_LINE_TYPE &&
      ((!!candidate.DiscountBackendID && candidate.DiscountBackendID === reward.BackendID) ||
        (!!candidate.DiscountCouponCode &&
          reward.coupons.some((coupon) => coupon.code === candidate.DiscountCouponCode)))
  );
  return line?.ID != null ? String(line.ID) : undefined;
}

export function findAppliedCouponLine<T extends CartLineLike>(lines: T[]): T | undefined {
  return lines.find((line) => line.Type === DISCOUNT_LINE_TYPE && !!line.DiscountCouponCode);
}

export function hasAppliedCouponOnCart(lines: CartLineLike[]): boolean {
  return !!findAppliedCouponLine(lines);
}

export function isRewardAppliedOnCart(lines: CartLineLike[], reward: LoyaltyReward): boolean {
  return !!findRewardDiscountLineId(lines, reward);
}

export function isRewardPendingOnCart(lines: CartLineLike[], reward: LoyaltyReward, hasOrder: boolean): boolean {
  return hasOrder && reward.coupons.some((coupon) => coupon.isApplied) && !isRewardAppliedOnCart(lines, reward);
}

export function isRewardAvailable(reward: LoyaltyReward): boolean {
  return reward.coupons.some((coupon) => coupon.available);
}

export function getRewardMissingRequirement(
  reward: LoyaltyReward
): { kind: "amount" | "points" | "products"; value: number } | undefined {
  const result = reward.ActionResult;
  if (!result || result.RequirementsMet) {
    return undefined;
  }
  if (result.OrderAmountMissing) {
    return { kind: "amount", value: result.OrderAmountMissing };
  }
  if (result.LoyaltyPointsMissing) {
    return { kind: "points", value: result.LoyaltyPointsMissing };
  }
  const quantityMissing = result.ProductSetResults?.reduce((total, set) => total + (set.SetQuantityMissing ?? 0), 0);
  if (quantityMissing) {
    return { kind: "products", value: quantityMissing };
  }
  return undefined;
}

export function getRewardCta(ctx: {
  applied: boolean;
  pending: boolean;
  hasCoupons: boolean;
  available: boolean;
  hasCartProducts: boolean;
  hasAppliedCoupon: boolean;
}): RewardCta {
  if (ctx.applied) {
    return "applied";
  }
  if (!ctx.hasCoupons) {
    return "noCoupon";
  }
  if (!ctx.hasCartProducts) {
    return "empty";
  }
  if (ctx.pending) {
    return "pending";
  }
  if (ctx.hasAppliedCoupon) {
    return "blocked";
  }
  if (ctx.available) {
    return "apply";
  }
  return "disabled";
}
